import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { logoutUserState } from 'store/auth/slices/AuthSlice';

import {
  addToCartAPI,
  getCartListAPI,
  removeCartItemAPI,
  updateCartQtyAPI,
} from '../actions/CustomerCartAction';

import { fetchHeaderFooterData } from './CustomerGlobalSlice';
// INITIAL STATE..........
// One entry per cart line whose price moved because an accepted offer was applied
// or ran out of its 24 hour window.
export interface CartOfferChange {
  cart_item_id: string;
  product_id: string;
  product_name: string;
  type: 'applied' | 'removed' | 'updated';
  previous_unit_price: number;
  unit_price: number;
}

interface CartState {
  cartInfo: any;
  cartItems: any[];
  groups: any[];
  singleCartInfo: any;
  singleCartItems: any[];
  singleGroups: any[];
  totalQuantity: number;
  offerChanges: CartOfferChange[];
  // Coupon discount quoted for the cart page (not part of the cart's own prices)
  couponDiscountAmount: number;
  couponPreviews: any[];
  loading: boolean;
}
const initialState: CartState = {
  cartInfo: null,
  cartItems: [],
  groups: [],
  singleCartInfo: null,
  singleCartItems: [],
  singleGroups: [],
  totalQuantity: 0,
  offerChanges: [],
  couponDiscountAmount: 0,
  couponPreviews: [],
  loading: false,
};

export const CustomerCartSlice = createSlice({
  name: 'customerProduct',
  initialState,
  reducers: {
    setCartTotalquantity(state, action: PayloadAction<number>) {
      state.totalQuantity = action.payload;
    },
    incrementCartTotalQuantity(state, action: PayloadAction<number>) {
      state.totalQuantity = Math.max(0, state.totalQuantity + action.payload);
    },
    // Called once the customer has seen the notice, so it does not follow them around
    clearCartOfferChanges(state) {
      state.offerChanges = [];
    },
    // Drops the cart lines (but not the badge count) so nothing keeps reading items that are gone,
    // e.g. product cards still showing a quantity for what was just checked out.
    clearCartLines(state) {
      state.cartInfo = null;
      state.cartItems = [];
      state.groups = [];
      state.couponDiscountAmount = 0;
      state.couponPreviews = [];
    },
    resetCartState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // RESET ON LOGOUT
      .addCase(logoutUserState, () => initialState)
      // FETCH
      .addCase(getCartListAPI.pending, (state, action) => {
        // A silent refresh updates the rows in place - showing the preloader would
        // blank the cart the customer is looking at.
        if (!(action.meta.arg as any)?._silentRefresh) {
          state.loading = true;
        }
      })
      .addCase(getCartListAPI.fulfilled, (state, action) => {
        const arg: any = action.meta.arg;
        const data = action.payload?.data || action.payload;
        state.loading = false;
        const isSingle =
          arg?.is_buy_now || arg?.cart_type === 'buy_now' || arg?.cart_type === 'single';
        if (isSingle) {
          state.singleCartInfo = data?.cart || null;
          state.singleCartItems = data?.cart_items || [];
          state.singleGroups = data?.groups || [];
        } else {
          state.cartInfo = data?.cart || null;
          state.cartItems = data?.cart_items || [];
          state.groups = data?.groups || [];
          state.totalQuantity = data?.total_quantity || 0;
          state.couponDiscountAmount = Number(data?.coupon_discount_amount || 0);
          state.couponPreviews = data?.coupon_previews || [];
        }
        // A refetch triggered by an offer re-price carries the changes in the action
        // that caused it, and would come back empty here - keep what is already shown.
        if (!arg?._silentRefresh) {
          state.offerChanges = data?.offer_changes || [];
        }
      })
      // update qty from global slice
      .addCase(fetchHeaderFooterData.fulfilled, (state, action) => {
        const data = action.payload;
        state.totalQuantity = data?.total_cart_quantity ?? 0;
      })
      // ADD
      .addCase(addToCartAPI.pending, (state, action) => {
        const arg: any = action.meta.arg;
        // Buy Now replaces the single cart on the server, so the previous Buy Now item held here
        // is already gone. Dropping it now keeps checkout from rendering (and quoting) that stale
        // item - with its old quantity - until checkout's own fetch of the new cart comes back.
        if (arg?.is_buy_now || arg?.cart_type === 'buy_now' || arg?.cart_type === 'single') {
          state.singleCartInfo = null;
          state.singleCartItems = [];
          state.singleGroups = [];
          state.loading = true;
        }
      })
      .addCase(addToCartAPI.rejected, (state, action) => {
        const arg: any = action.meta.arg;
        if (arg?.is_buy_now || arg?.cart_type === 'buy_now' || arg?.cart_type === 'single') {
          state.loading = false;
        }
      })
      .addCase(addToCartAPI.fulfilled, (state, action) => {
        const arg: any = action.meta.arg;
        const data = action.payload?.data || action.payload;
        if (!arg?.is_buy_now && arg?.cart_type !== 'buy_now' && arg?.cart_type !== 'single') {
          // state.totalQuantity = data.total_quantity ?? 0; // Handled optimistically
          if (data?.cart_item) {
            const index = state.cartItems.findIndex(
              (item: any) =>
                item.id === data.cart_item.id ||
                item.product?.id === data.cart_item.product?.id ||
                item.product_id === data.cart_item.product?.id
            );
            if (index !== -1) {
              state.cartItems[index] = data.cart_item;
            } else {
              state.cartItems.push(data.cart_item);
            }
          }
        }
        // Kept so the cart page can report a re-price that happened while adding,
        // which the cart list itself would no longer report.
        if ((data?.offer_changes || []).length > 0) {
          state.offerChanges = data.offer_changes;
        }
      })

      // UPDATE
      .addCase(updateCartQtyAPI.fulfilled, (state: any, action: any) => {
        const arg: any = action.meta.arg;
        const data = action.payload?.data || action.payload;
        const isSingle =
          arg?.is_buy_now || arg?.cart_type === 'buy_now' || arg?.cart_type === 'single';
        if (isSingle) {
          state.singleCartInfo = data.cart || state.singleCartInfo;
        } else {
          state.cartInfo = data.cart || state.cartInfo;
          // state.totalQuantity = data.total_quantity ?? 0; // Handled optimistically
        }

        state.offerChanges = data.offer_changes || [];

        const updatedItem = data.cart_item;

        if (updatedItem) {
          const targetArray = isSingle ? state.singleCartItems : state.cartItems;
          // The id can differ from the one that was sent: an update against a line that no longer
          // exists falls back to add to cart, which answers with a freshly created line for the
          // same product. Match on the product too so that row is replaced rather than duplicated.
          const index = targetArray.findIndex(
            (item: any) =>
              item.id === updatedItem.id ||
              item._id === updatedItem.id ||
              (updatedItem.product?.id &&
                (item.product?.id === updatedItem.product.id ||
                  item.product_id === updatedItem.product.id))
          );

          if (index !== -1) {
            const oldItem = targetArray[index];
            let mergedProduct = updatedItem.product;

            if (
              typeof updatedItem.product === 'object' &&
              updatedItem.product !== null &&
              typeof oldItem.product === 'object' &&
              oldItem.product !== null
            ) {
              mergedProduct = { ...oldItem.product, ...updatedItem.product };

              if (
                oldItem.product.ffl &&
                (!updatedItem.product.ffl || typeof updatedItem.product.ffl !== 'object')
              ) {
                mergedProduct.ffl = oldItem.product.ffl;
              }

              if (
                oldItem.product.vendor &&
                (!updatedItem.product.vendor || typeof updatedItem.product.vendor !== 'object')
              ) {
                mergedProduct.vendor = oldItem.product.vendor;
              }
            } else if (typeof updatedItem.product !== 'object') {
              mergedProduct = oldItem.product;
            }

            targetArray[index] = {
              ...oldItem,
              ...updatedItem,
              product: mergedProduct,
            };
          }
        }
      })

      // // REMOVE
      .addCase(removeCartItemAPI.fulfilled, (state, action) => {
        const arg: any = action.meta.arg;
        const data = action.payload?.data || action.payload;
        const isSingle =
          arg?.is_buy_now || arg?.cart_type === 'buy_now' || arg?.cart_type === 'single';
        if (isSingle) {
          state.singleCartInfo = data.cart;
        } else {
          state.cartInfo = data.cart;
          // state.totalQuantity = data.total_quantity ?? 0; // Handled optimistically
        }

        const removedItemId =
          typeof arg === 'object' && arg !== null ? arg.itemId || arg.cart_item_id || arg.id : arg;

        if (isSingle) {
          state.singleCartItems = state.singleCartItems.filter(
            (item: any) => item.id !== removedItemId && item._id !== removedItemId
          );
        } else {
          state.cartItems = state.cartItems.filter(
            (item: any) => item.id !== removedItemId && item._id !== removedItemId
          );
        }
      });
  },
});
export const {
  setCartTotalquantity,
  incrementCartTotalQuantity,
  clearCartOfferChanges,
  clearCartLines,
  resetCartState,
} = CustomerCartSlice.actions;
export default CustomerCartSlice.reducer;
